/**
 * SimilarPlayersFilterPanelContainer - Logic container for filter panel
 *
 * Creates stable callbacks for each sort option to avoid inline function
 * creation. SE-5570: the desktop sort dropdown now owns its own trigger via
 * the `SimilarPlayersSortDropdown` molecule, so this container no longer
 * builds a `renderSortTrigger` render-prop. The per-field callbacks stay
 * deferred via `setTimeout` because they are shared with the mobile inline
 * `SortOptionsList` path.
 * @remarks This is a Container component - handles render callbacks
 */

import React, { useMemo } from "react";

import SimilarPlayersFilterPanelView from "./SimilarPlayersFilterPanelView";

import {
  SimilarPlayersFilterData,
  SimilarPlayersFilterCallbacks,
  SortFieldCallbackMap,
} from "@/features/player-detail/types";
import { resolveOptionPickerProps } from "@/features/player-detail/utils/similarPlayersQueryState";

/**
 * Copy shown in a picker's option slot when its read FAILED, in place of the
 * picker's own "No <title>" copy — which is a claim about the DATA and is only
 * true once the read has actually succeeded (SE-7580).
 */
const LEAGUES_ERROR_MESSAGE = "Couldn't load leagues.";
const NATIONALITIES_ERROR_MESSAGE = "Couldn't load nationalities.";

/**
 * Disclosure shown alongside cached options whose refresh failed, so a list
 * that may be out of date is never presented as current (SE-7580).
 */
const LEAGUES_STALE_MESSAGE =
  "Couldn't refresh leagues — showing the last list we loaded.";
const NATIONALITIES_STALE_MESSAGE =
  "Couldn't refresh nationalities — showing the last list we loaded.";

/**
 * Props for SimilarPlayersFilterPanelContainer component
 */
interface SimilarPlayersFilterPanelContainerProps
  extends SimilarPlayersFilterData, SimilarPlayersFilterCallbacks {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Whether any filter has pending uncommitted changes requiring save */
  readonly hasPendingChanges: boolean;
  /** Whether filters are at their default values */
  readonly isDefaultFilters: boolean;
  /** Callback when Save button is pressed */
  readonly onSaveFilters: () => void;
  /** Callback when Reset button is pressed */
  readonly onResetFilters: () => void;
  /** Whether to render at full width without border (for modal contexts) */
  readonly isFullWidth?: boolean;
}

/**
 * Container component that provides render callbacks for filter panel
 * @param props - Container props
 * @param props.isDark - Whether dark mode is active
 * @param props.selectedSortLabel - Display label for selected sort option
 * @param props.sortOptions - Available sort options
 * @param props.onSortFieldChange - Callback when sort field changes
 * @param props.leaguesState - Resolution state of the leagues query
 * @param props.nationalitiesState - Resolution state of the nationalities query
 * @returns SimilarPlayersFilterPanelView with render callbacks
 */
const SimilarPlayersFilterPanelContainer = ({
  isDark,
  selectedSortLabel,
  sortOptions,
  onSortFieldChange,
  leaguesState,
  nationalitiesState,
  ...restProps
}: SimilarPlayersFilterPanelContainerProps) => {
  // SE-7580: flatten each picker's resolution state exactly once, here in the
  // container, so the views stay presentational. The resolver's exhaustive
  // switch is what stops a state added later from silently falling back to the
  // "no options" rendering.
  const leaguesPicker = useMemo(
    () =>
      resolveOptionPickerProps(
        leaguesState,
        LEAGUES_ERROR_MESSAGE,
        LEAGUES_STALE_MESSAGE
      ),
    [leaguesState]
  );
  const nationalitiesPicker = useMemo(
    () =>
      resolveOptionPickerProps(
        nationalitiesState,
        NATIONALITIES_ERROR_MESSAGE,
        NATIONALITIES_STALE_MESSAGE
      ),
    [nationalitiesState]
  );
  /** Defers onSortFieldChange so the RN Modal can finish closing before re-render (SE-3823) */
  const sortFieldCallbacks = useMemo<SortFieldCallbackMap>(
    () =>
      sortOptions.reduce(
        (acc, option) => ({
          ...acc,
          [option.field]: () => {
            setTimeout(() => onSortFieldChange(option.field), 0);
          },
        }),
        {} as SortFieldCallbackMap
      ),
    [sortOptions, onSortFieldChange]
  );

  return (
    <SimilarPlayersFilterPanelView
      isDark={isDark}
      selectedSortLabel={selectedSortLabel}
      sortOptions={sortOptions}
      onSortFieldChange={onSortFieldChange}
      sortFieldCallbacks={sortFieldCallbacks}
      leaguesPicker={leaguesPicker}
      nationalitiesPicker={nationalitiesPicker}
      {...restProps}
    />
  );
};

SimilarPlayersFilterPanelContainer.displayName =
  "SimilarPlayersFilterPanelContainer";

export default React.memo(SimilarPlayersFilterPanelContainer);
